import { call, put, select, takeLatest } from 'redux-saga/effects';
import _ from 'lodash';
import APP from 'types/app';
import API from 'types/api';
import PLAYER from 'types/player';
import { PATH } from 'types/router';
import apiActions from 'actions/api';
import appActions from 'actions/app';
import routerActions from 'actions/router';
import { needToLoadSubscription, subscriptionInfo } from 'selectors/subscription';
import { needToLoadVideoCollection } from 'selectors/videoCollection';
import {
  dataReadySelector,
  idSelector,
  pathnameSelector,
  userRequestedOnStartupSelector,
} from 'selectors/app';
import { movieErrorInfo, movieInfo } from 'selectors/movie';
import {
  getIsUserLoading,
  isUserLoggedInSelector,
} from 'selectors/user';
import { getVideoStream, getPlayerStream } from '../selectors/player';
import { language, publicDomain } from '../selectors/configuration';
import { isMobile as isMobileSelector } from '../selectors/userAgent';
import mappingMovieRestrictionToErrors from '../restrictions/mappingMovieRestrictionToErrors';
import mappingStreamRestrictionToErrors from '../restrictions/mappingStreamRestrictionToErrors';
import { BIZ_CLASS } from '../restrictions/constants';

const debug = self.debug('Sagas', 'App');

export function* updateData({ type, payload }) {
  const isLoggedIn = yield select(isUserLoggedInSelector);
  const movie = yield select(movieInfo, {});
  const {
    isLoading = false,
    isError = false,
  } = yield select(movieErrorInfo, {});
  const id = yield select(idSelector);
  const isUserLoading = yield select(getIsUserLoading);
  const pathname = yield select(pathnameSelector);
  const userRequestedOnStartup = yield select(userRequestedOnStartupSelector);

  debug('updateData: %O', { type, id, isLoggedIn, movie, isUserLoading, pathname });

  if (
    id &&
    ((type === API.GET_USER_SUCCESS && isLoggedIn && pathname && !userRequestedOnStartup) ||
      (type === API.POST_AUTH_SUCCESS && isLoggedIn) ||
      (type === API.POST_REGISTRATION_SUCCESS && isLoggedIn) ||
      (type === API.POST_LOGOUT_SUCCESS && !isLoggedIn) ||
      (type === APP.SET_PATHNAME &&
        payload !== PATH.PLAYER &&
        _.isEmpty(movie) &&
        !isLoading &&
        !isError &&
        !isUserLoading) ||
      type === APP.FORCE_DATA_UPDATE)
  ) {
    debug('reload data');
    yield call(resetDataReady);
    yield put(appActions.resetSubscriptionVideoCollections());
    yield put(apiActions.getVideoInfo(id, { root_object: true }));
  }
}

export function* resetDataReady() {
  const dataReady = yield select(dataReadySelector);
  if (dataReady) {
    yield put(appActions.setDataReady(false));
  }
}

export function* onGetMovieInfoSuccess() {
  const id = yield select(idSelector);
  const loadSubscription = yield select(needToLoadSubscription, { id });
  if (loadSubscription) {
    yield put(apiActions.getSubscriptionInfo());
  } else {
    yield put(appActions.setDataReady(true));
  }
}

export function* onGetSubscriptionInfoSuccess() {
  const id = yield select(idSelector);
  const idObj = { id };
  const loadVideoCollection = yield select(needToLoadVideoCollection, idObj);
  if (loadVideoCollection) {
    const info = yield select(subscriptionInfo, idObj);
    yield put(apiActions.getVideoCollection(info.collectionId));
  } else {
    yield put(appActions.setDataReady(true));
  }
}

export function* onGetVideoCollectionSuccess() {
  yield put(appActions.setDataReady(true));
}

export function* onPlayerMediaError({ payload }) {
  debug('onPlayerMediaError: payload: %O', payload);
  const {
    data: {
      fatal = false,
    } = {},
  } = payload;

  if (!fatal) {
    debug('Non fatal player error. Maybe loading advertise failure.');
    return;
  }

  let stream = yield select(getPlayerStream);
  if (!stream) {
    if (payload.data.stream) {
      stream = payload.data.stream;
    } else {
      debug('onPlayerMediaError: empty stream');
      stream = {};
    }
  }
  const { restrictions = [] } = stream;
  const [restriction = {}] = restrictions;
  const errorType = mappingStreamRestrictionToErrors.get(restriction.code);
  const domain = yield select(publicDomain);
  const lang = yield select(language);
  const isMobile = yield select(isMobileSelector);
/*
// TODO: get prev/next series/program from state
  const nextProgram = currentChannel.getNextProgram(playingProgram);
  const next = yield select(getNext);
  const prev = yield select(getPrev);
*/

  const params = {
    mggURL: `${domain}/${lang}`,
    isMobile,
    get target() {
      return stream;
    },
    message: restriction.message,
  };

  debug('onPlayerMediaError: restriction code: "%s" resolved to "%s"', restriction.code, errorType);

  if (restriction.code === BIZ_CLASS) {
    yield put(routerActions.goTo(PATH.INFO));
    return;
  }
  yield put(routerActions.goToErrorPage(errorType, { params }));
}

function* validateMovieRestrictions({ response }) {
  const { data, data: { restrictions = [] } } = response;
  const [restriction] = restrictions;

  if (!restriction) {
    return;
  }

  const errorType = mappingMovieRestrictionToErrors.get(restriction.code);

  if (!errorType) {
    return;
  }

  const currentPage = yield select(pathnameSelector);

  /**
   * Roll out advertise in any case.
   * ErrorPage will be opened on stream error from onPlayerMediaError
   */
  if ([PATH.PLAYER, PATH.UNIVERSAL].includes(currentPage)) {
    return;
  }

  debug('validateRestrictions %O', restriction);
  const isMobile = yield select(isMobileSelector);

  const params = {
    isMobile,
    get target() {
      return data;
    },
    message: restriction.message,
  };

  yield put(routerActions.goToErrorPage(errorType, { params }));
}

function* openUniversalErrorPage({ type, payload }) {
  debug('Open ErrorPage due to: %s %O', type, payload.response)
  yield put(routerActions.goToErrorPage());
}

function* onSetVideoId({ payload: id }) {
  if (!id) {
    return;
  }

  const movie = yield select(movieInfo, { id });

  if (_.isEmpty(movie)) {
    debug('start fetching video info for id: %s', id);
    yield put(apiActions.getVideoInfo(id, { root_object: true }));
  }
}

/**
 * So far embed does not have any visible tab or window,
 * it could have a reason only for SEO,
 * sure only in case G would to adopt updates after loading.
 */
function* updateDocumentTitle({ payload: dataReady }) {
  if (!dataReady) {
    return;
  }

  yield call(setDocumentTitle, yield select(movieInfo, {})) ||
  call(setDocumentTitle, yield select(getVideoStream));
}

function setDocumentTitle({ title }) {
  if (!title) {
    return { done: false };
  }

  if (document.title === title) {
    return { done: true };
  }

  document.title = title;
  debug('update document title to "%s"', document.title);

  return { done: true };
}

function setupBodyClassList({ payload }) {
  const {
    device: { type },
    os: { name },
  } = payload;

  type && document.body.classList.add(type.replace(/\s/, ''));
  name && document.body.classList.add(name.replace(/\s/, ''));
}

function* resolveStreamError(action) {
  const pathname = yield select(pathnameSelector);
  if (pathname === PATH.UNIVERSAL) {
    yield put(routerActions.goTo(PATH.INFO));
  } else {
    yield openUniversalErrorPage(action);
  }
}

export default function* appSagas() {
  yield takeLatest(APP.SET_UA, setupBodyClassList);
  yield takeLatest(APP.SET_ID, onSetVideoId);
  yield takeLatest(APP.SET_DATA_READY, updateDocumentTitle);
  yield takeLatest(API.GET_USER_SUCCESS, updateData);
  yield takeLatest(API.POST_REGISTRATION_SUCCESS, updateData);
  yield takeLatest(API.POST_AUTH_SUCCESS, updateData);
  yield takeLatest(API.POST_LOGOUT_SUCCESS, updateData);
  yield takeLatest(APP.FORCE_DATA_UPDATE, updateData);
  yield takeLatest(APP.SET_PATHNAME, updateData);
  yield takeLatest(API.GET_VIDEO_INFO, resetDataReady);
  yield takeLatest(API.GET_VIDEO_INFO_SUCCESS, onGetMovieInfoSuccess);
  yield takeLatest(API.GET_VIDEO_INFO_SUCCESS, validateMovieRestrictions);
  yield takeLatest(PLAYER.HANDLE_MEDIA_ERROR_EVENT, onPlayerMediaError);
  yield takeLatest(PLAYER.ERROR, onPlayerMediaError);
  yield takeLatest(API.GET_VIDEO_INFO_ERROR, openUniversalErrorPage);
  yield takeLatest(API.GET_STREAM_ERROR, resolveStreamError);
  yield takeLatest(API.GET_SUBSCRIPTION_INFO_SUCCESS, onGetSubscriptionInfoSuccess);
  yield takeLatest(API.GET_VIDEO_COLLECTION_SUCCESS, onGetVideoCollectionSuccess);
}
